/**
 * Browser-based currency detection for Stripe pricing display.
 * Falls back to USD when the detected currency is not supported.
 */

export const SUPPORTED_CURRENCIES = [
  "USD",
  "GBP",
  "EUR",
  "AUD",
  "NZD",
  "CAD",
] as const;

export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

function isSupportedCurrency(code: string): code is SupportedCurrency {
  return (SUPPORTED_CURRENCIES as ReadonlyArray<string>).includes(code);
}

/**
 * Map a BCP 47 locale string to a supported ISO 4217 currency code.
 */
export function localeToCurrency(locale: string): string {
  const normalized = locale.replace("_", "-");
  const lower = normalized.toLowerCase();
  const [lang, region] = lower.split("-");

  if (region === "gb") return "GBP";
  if (region === "au") return "AUD";
  if (region === "nz") return "NZD";
  if (region === "ca") return "CAD";

  if (lower === "en-gb" || lower.startsWith("en-gb-")) return "GBP";

  const euroRegions = new Set([
    "at",
    "be",
    "cy",
    "ee",
    "fi",
    "fr",
    "de",
    "gr",
    "ie",
    "it",
    "lv",
    "lt",
    "lu",
    "mt",
    "nl",
    "pt",
    "sk",
    "si",
    "es",
  ]);
  if (region && euroRegions.has(region)) return "EUR";

  const euroLangs = new Set([
    "de",
    "fr",
    "es",
    "it",
    "nl",
    "pt",
    "fi",
    "el",
    "sk",
    "sl",
    "et",
    "lv",
    "lt",
    "mt",
    "ga",
  ]);
  if (euroLangs.has(lang) && lang !== "pt") return "EUR";
  if (lang === "pt" && region === "pt") return "EUR";

  if (lower.startsWith("en-au")) return "AUD";
  if (lower.startsWith("en-nz")) return "NZD";
  if (lower.startsWith("en-ca")) return "CAD";

  return "USD";
}

/**
 * Best-effort currency from the user's environment (client-only).
 */
export function detectUserCurrency(): SupportedCurrency {
  if (typeof window === "undefined") return "USD";

  try {
    const resolved = new Intl.NumberFormat().resolvedOptions();
    if (resolved.currency && isSupportedCurrency(resolved.currency))
      return resolved.currency;

    const locale =
      resolved.locale ||
      (typeof navigator !== "undefined" ? navigator.language : "") ||
      "en-US";
    const fromLocale = localeToCurrency(locale);
    return isSupportedCurrency(fromLocale) ? fromLocale : "USD";
  } catch {
    return "USD";
  }
}

export function getLocaleFromCurrency(currency: SupportedCurrency): string {
  if (currency === "GBP") return "en-GB";
  if (currency === "AUD") return "de-DE";
  if (currency === "NZD") return "de-DE";
  if (currency === "CAD") return "de-DE";
  if (currency === "EUR") return "de-DE";
  return "en-US";
}
